import React from 'react';
import styled from '@emotion/styled';
import { spacing } from '../../style-utils/theme';
import { ChipGroup, ToggleChip } from '../toggle-chip/toggle-chip';

/**
 * WHICH DAYS OF THE WEEK — seven toggle chips and two shortcuts.
 *
 * The value is an array of ISO weekday numbers (1 = Monday … 7 = Sunday), the
 * same numbering the server stores, so nothing between this control and the
 * API has to translate. The chips run Monday first, the en-GB week the rest of
 * the product formats dates in.
 *
 * Each chip shows the short name and carries the full one as its accessible
 * name: "Mon" is what fits seven-across on a phone, "Monday" is what a screen
 * reader should say. The visible text is the start of the name, so voice
 * control ("click Mon") still finds it.
 *
 * The shortcuts are toggles too, pressed when the value is exactly that set.
 * Pressing one sets it; pressing it again clears the days, which is what a
 * toggle that is already on is expected to do.
 */

const DAYS: ReadonlyArray<{ iso: number; short: string; long: string }> = [
  { iso: 1, short: 'Mon', long: 'Monday' },
  { iso: 2, short: 'Tue', long: 'Tuesday' },
  { iso: 3, short: 'Wed', long: 'Wednesday' },
  { iso: 4, short: 'Thu', long: 'Thursday' },
  { iso: 5, short: 'Fri', long: 'Friday' },
  { iso: 6, short: 'Sat', long: 'Saturday' },
  { iso: 7, short: 'Sun', long: 'Sunday' },
];

export const WEEKDAYS: readonly number[] = [1, 2, 3, 4, 5];
export const EVERY_DAY: readonly number[] = [1, 2, 3, 4, 5, 6, 7];

/** Unique, in-range, ascending — the one shape this control ever emits. */
const normalise = (days: readonly number[]): number[] => {
  const valid = days.filter(d => Number.isInteger(d) && d >= 1 && d <= 7);

  return Array.from(new Set(valid)).sort((a, b) => a - b);
};

const sameSet = (a: readonly number[], b: readonly number[]): boolean =>
  a.length === b.length && a.every((d, i) => d === b[i]);

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
`;

export interface WeekdayPickerProps {
  /** ISO weekday numbers, 1 = Monday … 7 = Sunday. */
  value: readonly number[];
  onChange: (next: number[]) => void;
  /** Accessible name of the day set, e.g. "Visiting days". */
  label: string;
  disabled?: boolean;
}

export const WeekdayPicker: React.FC<WeekdayPickerProps> = ({
  value,
  onChange,
  label,
  disabled = false,
}) => {
  const selected = normalise(value);
  const isWeekdays = sameSet(selected, WEEKDAYS);
  const isEveryDay = sameSet(selected, EVERY_DAY);

  const toggleDay = (iso: number, on: boolean) => {
    onChange(normalise(on ? [...selected, iso] : selected.filter(d => d !== iso)));
  };

  return (
    <Stack>
      <ChipGroup aria-label={label}>
        {DAYS.map(day => (
          <ToggleChip
            key={day.iso}
            aria-label={day.long}
            pressed={selected.includes(day.iso)}
            onToggle={on => toggleDay(day.iso, on)}
            disabled={disabled}
          >
            {day.short}
          </ToggleChip>
        ))}
      </ChipGroup>
      <ChipGroup aria-label={`${label} shortcuts`}>
        <ToggleChip
          pressed={isWeekdays}
          onToggle={on => onChange(on ? [...WEEKDAYS] : [])}
          disabled={disabled}
        >
          Weekdays
        </ToggleChip>
        <ToggleChip
          pressed={isEveryDay}
          onToggle={on => onChange(on ? [...EVERY_DAY] : [])}
          disabled={disabled}
        >
          Every day
        </ToggleChip>
      </ChipGroup>
    </Stack>
  );
};
